import { useStorageUpload } from '@/db/useStorageUpload';
import { readImageDimensions } from '@/util/imageDimensions';
import logger from '@/util/logger';
import { createId, storage } from '@rock/db-admin';
import type { BrandId, BrandLogoImage } from '@rock/types';
import { deleteObject, getDownloadURL, getMetadata, listAll, ref } from 'firebase/storage';
import { useCallback, useEffect, useState } from 'react';

const log = logger('useBrandUploads');

/** Extensions we can name a staged upload with, by the content type the file picker reports. */
const EXTENSIONS: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
};

/** What the file input accepts — the formats `rehostBrandAsset` can actually derive renditions from. */
export const BRAND_UPLOAD_ACCEPT = Object.keys(EXTENSIONS).join(',');

/** One image an admin uploaded for this brand, as listed from Storage. */
export interface BrandUpload {
    /**
     * The upload's identity, doubling as the assigned asset's `id`: stable across assignment and
     * re-derivation (`rehostBrandAsset` spreads the asset through), so the picker can mark which upload
     * a slot is currently showing even after the trigger has replaced its URLs.
     */
    id: string;
    /** Full Storage object path — what deletes it. */
    path: string;
    url: string;
    fileName: string;
    width: number;
    height: number;
    /** Storage's own creation stamp — what orders the grid; nothing else reads it. */
    timeCreated: string;
}

/**
 * The brand's uploaded images, indexed by nothing but their own Storage prefix.
 *
 * There is deliberately no Firestore mirror of this list. An upload's whole lifecycle is "it exists
 * under `brands/{id}/uploads/`, so it shows up here and can be assigned or deleted" — which makes an
 * orphan (bytes nobody can see or remove) impossible by construction, and spares us reconciling a doc
 * against a bucket that can disagree with it.
 *
 * Dimensions ride along in the object's `customMetadata` rather than being re-derived on every listing;
 * they're only a preview aid anyway, since the `rehostBrandLogo` trigger recomputes them from the
 * pixels it actually stores.
 */
export function useBrandUploads(brandId: BrandId | undefined) {
    const [uploads, setUploads] = useState<BrandUpload[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const { uploadFile } = useStorageUpload();

    const folder = brandId ? uploadsFolder(brandId) : undefined;

    const reload = useCallback(async () => {
        if (!folder) return;
        setIsLoading(true);
        try {
            const listing = await listAll(ref(storage, folder));
            const items = await Promise.all(
                listing.items.map(async (item) => {
                    const [url, metadata] = await Promise.all([getDownloadURL(item), getMetadata(item)]);
                    return {
                        id: item.name.replace(/\.[^.]+$/, ''),
                        path: item.fullPath,
                        url,
                        fileName: metadata.customMetadata?.originalName ?? item.name,
                        width: Number(metadata.customMetadata?.width ?? 0),
                        height: Number(metadata.customMetadata?.height ?? 0),
                        timeCreated: metadata.timeCreated,
                    };
                }),
            );
            // Newest first — the one you just uploaded is the one you're about to assign.
            items.sort((a, b) => b.timeCreated.localeCompare(a.timeCreated));
            setUploads(items);
        } catch (err) {
            log.error('Failed to list brand uploads', { err, folder });
        } finally {
            setIsLoading(false);
        }
    }, [folder]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const upload = useCallback(
        async (file: File) => {
            if (!folder) return;
            setIsUploading(true);
            try {
                const { width, height } = await readImageDimensions(file);
                const uploadId = createId();
                const extension = EXTENSIONS[file.type] ?? 'png';
                await uploadFile(file, `${folder}/${uploadId}.${extension}`, {
                    contentType: file.type,
                    customMetadata: { originalName: file.name, width: `${width}`, height: `${height}` },
                });
                await reload();
            } finally {
                setIsUploading(false);
            }
        },
        [folder, reload, uploadFile],
    );

    const remove = useCallback(
        async (upload: BrandUpload) => {
            await deleteObject(ref(storage, upload.path));
            await reload();
        },
        [reload],
    );

    return { uploads, isLoading, isUploading, upload, remove } as const;
}

/**
 * The assignable asset for an upload. `source: 'upload'` is its true provenance and survives assignment
 * (only `pinned` is stamped there); `thumbnail` points at the upload itself as a placeholder, which the
 * `rehostBrandLogo` trigger replaces within seconds with a thumbnail rendered from the primary's own
 * pixels — never a copy, which is what keeps a transparent logo from previewing as a white box on the
 * app's small surfaces.
 */
export function uploadToAsset(upload: BrandUpload): BrandLogoImage {
    return {
        id: upload.id,
        url: upload.url,
        thumbnail: upload.url,
        width: upload.width,
        height: upload.height,
        source: 'upload',
    };
}

function uploadsFolder(brandId: BrandId): string {
    return `brands/${brandId}/uploads`;
}
